import React, { Suspense, useContext, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, EffectFade } from "swiper";

import MediaTextContext from "../MediaTextContext";

import { Media } from "../../../components/Media/Media";
import { Button } from "../../../components/Button";
import { Grid } from "../../../components/Grid/Grid";

import { useIsTablet } from "../../../lib/hooks/mediaQueries";
import { generateClassString as gcs } from "../../../lib/helper/generateClassString";

import * as styles from "./mediaContent.module.less";
import t from "../../../lib/translate";
import GeneralPageContext from "../../../templates/GeneralPageContext";
import { useCallback } from "react";

const MediaGallery = React.lazy(() =>
    import("../../../components/media-gallery/MediaGallery").then((module) => {
        return { default: module.MediaGallery };
    })
);

function MediaContent({
    id,
    mediaElements,
    hideGrid,
    withGallery,
    galleryImages,
    thumbnails
}) {
    const idPaginationElement = "pagination-element-".concat(id);
    const idButtonPrev = "button-prev-".concat(id);
    const idButtonNext = "button-next-".concat(id);
    const isTablet = useIsTablet();

    const [activeElement, setActiveElement] = useState(0);

    const [galleryOpen, setGalleryOpen] = useState(false);

    const toggleGallery = useCallback(() => {
        setGalleryOpen((currentState) => !currentState);
    }, []);

    const slideChange = useCallback(({ realIndex }) => {
        setActiveElement(realIndex);
    }, []);

    const {
        layout: { showAnchorNavigation }
    } = useContext(GeneralPageContext);

    return (
        <MediaTextContext.Provider value={{ activeElement }}>
            <div
                className={gcs(
                    styles.mediaGalleryContainer,
                    [mediaElements.length === 1, styles.singleImage],
                    [showAnchorNavigation, styles.anchored]
                )}
            >
                {mediaElements.length > 1 ? (
                    <>
                        {!hideGrid && (
                            <Grid
                                className={styles.sliderGrid}
                                size={isTablet ? "s" : "m"}
                            />
                        )}
                        <Swiper
                            modules={[Navigation, Pagination, EffectFade]}
                            effect="fade"
                            fadeEffect={{
                                crossFade: true
                            }}
                            navigation={{
                                prevEl: `#${idButtonPrev}`,
                                nextEl: `#${idButtonNext}`
                            }}
                            pagination={{
                                type: "fraction",
                                el: `#${idPaginationElement}`,
                                renderFraction: function (
                                    currentClass,
                                    totalClass
                                ) {
                                    return (
                                        '<span class="' +
                                        currentClass +
                                        '"></span>' +
                                        " | " +
                                        '<span class="' +
                                        totalClass +
                                        '"></span>'
                                    );
                                }
                            }}
                            onSlideChange={slideChange}
                            loop={true}
                            className={styles.mediaTextSwiper}
                        >
                            {mediaElements.map((mediaElement, index) => {
                                const {
                                    media: { isImage }
                                } = mediaElement;
                                return (
                                    <SwiperSlide key={mediaElement.id}>
                                        {withGallery && (
                                            <button
                                                className={gcs([
                                                    isImage,
                                                    styles.buttonForImage,
                                                    styles.buttonForVideo
                                                ])}
                                                onClick={() =>
                                                    toggleGallery(index)
                                                }
                                            />
                                        )}

                                        <Media
                                            eventKey={index}
                                            mediaElement={mediaElement}
                                            videoControls={true}
                                            playButtonClass={styles.playButton}
                                            gridDisable={true}
                                            gridSize={isTablet ? "s" : "m"}
                                            withGallery={withGallery}
                                            {...(withGallery
                                                ? { showDescription: false }
                                                : {})}
                                        />
                                    </SwiperSlide>
                                );
                            })}
                        </Swiper>

                        <div className={styles.mediaSliderControls}>
                            <div
                                id={idPaginationElement}
                                className={styles.paginationElement}
                            />
                            <div className={styles.slideIcons}>
                                <Button
                                    id={idButtonPrev}
                                    type="prev"
                                    aria-label={t("navigation.button.prev")}
                                />
                                <Button
                                    id={idButtonNext}
                                    type="next"
                                    aria-label={t("navigation.button.next")}
                                />
                            </div>
                        </div>
                    </>
                ) : (
                    <Media
                        mediaElement={mediaElements[0]}
                        videoControls={true}
                        playButtonClass={styles.playButton}
                        gridDisable={hideGrid}
                        gridSize={isTablet ? "s" : "m"}
                    />
                )}
            </div>

            <Suspense fallback={<div>Loading...</div>}>
                {withGallery && galleryOpen && (
                    <MediaGallery
                        galleryImages={galleryImages}
                        thumbnails={thumbnails}
                        toggleFunction={toggleGallery}
                        initialElement={
                            activeElement < 0
                                ? mediaElements.length + activeElement
                                : activeElement
                        }
                    />
                )}
            </Suspense>
        </MediaTextContext.Provider>
    );
}

export { MediaContent };
